/* Clinical Governance — how recommendations are kept current + evidence validation */
import React from "react";
import { useComplianceCatalog } from "@/lib/compliance";
import { useLanguage } from "@/i18n/LanguageContext";
import { Microscope, RefreshCw, BookOpen, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";
import AppFooter from "@/components/AppFooter";

export default function ClinicalGovernancePage() {
  const { catalog } = useComplianceCatalog();
  const { language } = useLanguage();
  const bg = language === "bg";
  const gov = catalog?.clinical_governance;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-emerald-50">
      <SEO
        title={bg ? "Клинично управление — Nutramilo" : "Clinical Governance — Nutramilo"}
        description={bg ? "Как се обновяват клиничните препоръки, процес на валидация с доказателства и cикли за преглед." : "How clinical recommendations are updated, evidence validation process and review cycles."}
      />
      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="clinical-governance-page">
        <a href="/" className="text-xs text-sky-700 hover:underline">{bg ? "← Обратно" : "← Back"}</a>
        <h1 className="text-3xl font-bold text-gray-900 mt-3 mb-2 flex items-center gap-2">
          <Microscope className="text-emerald-600" size={26} />
          {bg ? "Клинично управление" : "Clinical Governance"}
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          {bg
            ? "Политика за клинично качество — как поддържаме правилата актуални, проверени и прозрачни."
            : "Clinical quality policy — how we keep rules up-to-date, verified and transparent."}
        </p>

        <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-start gap-3">
          <RefreshCw size={18} className="text-emerald-700 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-emerald-900">{bg ? "Цикъл на преглед" : "Review cycle"}</div>
            <div className="text-xs text-emerald-900 mt-0.5">{gov?.review_cycle}</div>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <BookOpen size={18} className="text-sky-600" />
            {bg ? "Източници на доказателства" : "Evidence sources"}
          </h2>
          <div className="flex flex-wrap gap-2">
            {(gov?.evidence_sources || []).map(s => (
              <span key={s} className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-800">
                {s}
              </span>
            ))}
          </div>
        </section>

        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{bg ? "Процес на обновяване на моделите" : "Model update process"}</h2>
          <p className="text-sm text-gray-700 leading-relaxed">{gov?.model_updates}</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            {bg ? "Признати ограничения" : "Known limitations"}
          </h2>
          <ul className="space-y-1.5 text-sm text-gray-700">
            {(gov?.limitations || []).map((l, i) => (
              <li key={`${i}-${(l || '').slice(0, 24)}`} className="flex gap-2">
                <span className="text-amber-500 mt-0.5">•</span><span>{l}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
